import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { NoteBlank, FloppyDisk } from "@phosphor-icons/react";
import { OverviewCard } from "./Card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Extra widget (d) — Note rapida coach (sticky note privata).
 * Persiste sul campo `coach_quick_note` del cliente. Salvataggio con debounce
 * + pulsante Salva manuale per feedback immediato.
 */
export function CoachNoteWidget({ clientId, initialNote }) {
  const [value, setValue] = useState(initialNote || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const debounceRef = useRef(null);

  // Keep input in sync if parent re-fetches the client.
  useEffect(() => {
    setValue(initialNote || "");
    setDirty(false);
  }, [initialNote]);

  const persist = async (nextValue) => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, { coach_quick_note: nextValue });
      setDirty(false);
    } catch (err) {
      console.error("[CoachNote] save failed", err);
      toast.error("Impossibile salvare la nota");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    setDirty(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist(next), 1200);
  };

  const handleSaveNow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    persist(value);
  };

  return (
    <OverviewCard
      title="Nota rapida"
      icon={NoteBlank}
      iconColor="text-[#FFCC00]"
      testId="overview-coach-note"
      accent={saving ? "salvataggio…" : (dirty ? "non salvato" : null)}
      action={
        <button
          type="button"
          onClick={handleSaveNow}
          disabled={!dirty || saving}
          className="text-[11px] px-2 py-1 rounded-md bg-[#171717] border border-[#262626] text-zinc-400 hover:text-white disabled:opacity-50 flex items-center gap-1"
          data-testid="overview-coach-note-save"
        >
          <FloppyDisk size={12} /> Salva
        </button>
      }
    >
      <textarea
        value={value}
        onChange={handleChange}
        placeholder="Promemoria, osservazioni, idee per la prossima scheda…"
        rows={4}
        className="w-full bg-[#171717] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#FFCC00] resize-y"
        data-testid="overview-coach-note-input"
      />
      <p className="text-[10px] text-zinc-600 mt-1">Visibile solo a te. Salvataggio automatico.</p>
    </OverviewCard>
  );
}
